// ============================================================================
// CLEO'S SECRET — JournalPage
// File: src/pages/JournalPage.jsx
//
// Editorial rewrite following the landing-page section template + Stage 2
// formatting rules. Four-section structure:
//   1. Hero (cream / SAND)         — page opener
//   2. Articles (dark / OBS)       — category filter + 2-column grid of
//                                    editorial article cards
//   3. CTA (cream / SAND)          — call to begin the conversation
//   4. Founding principle (dark)   — closing canonical Ankh + premise + label
//
// No em dashes in user-facing content. Body uses T.bodyLg justified with
// hyphens. Labels and eyebrows use Cinzel weights per Stage 2 rules.
// ============================================================================

import React from 'react';
import {
  FF, P, T,
  SECTION_PADDING, CONTAINER_MAX, CONTAINER_READING,
  Eyebrow, SectionDivider, SectionHeadline,
} from '../lib/design-tokens';
import InvitationBlock from '../components/marketing/InvitationBlock';

// ───────── Article content (em-dash free) ─────────
const ARTICLES = [
  {
    category: 'Hormonal health',
    title: "Your bloods are normal. So why do you feel terrible?",
    excerpt: "The gap between pathology reference ranges and optimal health is where most women's symptoms live. We explain why 'normal' results can still mean something is wrong, and what to ask for instead.",
    date: 'April 2026',
    readTime: '6 min read',
  },
  {
    category: 'Perimenopause',
    title: 'The perimenopause checklist every woman over 38 should know',
    excerpt: "Perimenopause does not start with hot flushes. It starts with subtle shifts: sleep changes, brain fog, a shorter fuse. Here are the twelve signs your GP might not connect to hormones.",
    date: 'April 2026',
    readTime: '8 min read',
  },
  {
    category: 'Clinical insight',
    title: 'Iron, ferritin, and the fatigue mystery',
    excerpt: "Your iron levels might be 'in range', but your ferritin (the storage form) tells a completely different story. Why this single biomarker is the most commonly missed cause of exhaustion in women.",
    date: 'March 2026',
    readTime: '5 min read',
  },
  {
    category: 'Lifestyle',
    title: 'Why high-performing women burn out differently',
    excerpt: "The cortisol patterns we see in executive women tell a specific story: not acute stress, but a slow erosion of resilience. Here is what the science says about stress, hormones, and recovery.",
    date: 'March 2026',
    readTime: '7 min read',
  },
  {
    category: 'Nutrition',
    title: 'The supplements worth taking, and the ones wasting your money',
    excerpt: "The supplement industry is worth billions, and most of it is noise. Based on what we see in clinical practice, here are the evidence-backed supplements that actually move the needle for women over 35.",
    date: 'February 2026',
    readTime: '9 min read',
  },
  {
    category: 'Sleep',
    title: '3am wake-ups: what your body is actually telling you',
    excerpt: "Waking between 2am and 4am is one of the most common symptoms we hear about. It is not random. It is often a cortisol pattern with a specific hormonal signature. Here is what is happening and what to do.",
    date: 'February 2026',
    readTime: '5 min read',
  },
];

const CATEGORIES = ['All', 'Hormonal health', 'Perimenopause', 'Clinical insight', 'Lifestyle', 'Nutrition', 'Sleep'];

// ───────── CategoryPill — filter button ─────────
// Editorial Cormorant 700 throughout. Single canonical gold (GO #C9A961)
// across active and inactive states — opacity differentiates, not weight or
// hue. Active = full-opacity GO on faint gold tint; inactive = 0.55 GO on
// transparent. Per design-spec.md Section 11 (Cinzel reserved for the brand
// wordmark only).
function CategoryPill({ label, active, onClick }) {
  var bg     = active ? 'rgba(201,169,97,0.10)' : 'transparent';
  var border = active ? P.GO : 'rgba(201,169,97,0.32)';
  var color  = active ? P.GO : 'rgba(201,169,97,0.55)';
  var onHover = function(e) {
    if (active) return;
    e.currentTarget.style.borderColor = P.GO;
    e.currentTarget.style.color = P.GO;
  };
  var onLeave = function(e) {
    if (active) return;
    e.currentTarget.style.borderColor = 'rgba(201,169,97,0.32)';
    e.currentTarget.style.color = 'rgba(201,169,97,0.55)';
  };
  return (
    <button
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.9vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.26em',
        textTransform: 'uppercase',
        padding: 'clamp(8px, 0.9vw, 11px) clamp(14px, 1.5vw, 22px)',
        background: bg,
        color: color,
        border: '1px solid ' + border,
        cursor: 'pointer',
        transition: 'all 0.25s',
      }}>
      {label}
    </button>
  );
}

// ───────── ArticleCard — editorial preview card ─────────
function ArticleCard({ category, title, excerpt, date, readTime }) {
  var onHover = function(e) {
    e.currentTarget.style.borderColor = P.GO2;
    e.currentTarget.style.transform = 'translateY(-2px)';
  };
  var onLeave = function(e) {
    e.currentTarget.style.borderColor = 'rgba(201,169,97,0.22)';
    e.currentTarget.style.transform = 'translateY(0)';
  };
  return (
    <article
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        background: 'rgba(210,184,134,0.04)',
        border: '1px solid rgba(201,169,97,0.22)',
        padding: 'clamp(32px, 3.4vw, 48px) clamp(28px, 3vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 18,
        transition: 'border-color 0.3s, transform 0.3s',
      }}>
      {/* Category label */}
      <div style={{
        ...T.label,
        fontSize: 'clamp(10px, 0.85vw, 12px)',
        letterSpacing: '0.3em',
        color: P.GO2,
      }}>{category}</div>

      {/* Title */}
      <h3 style={{
        fontFamily: FF,
        fontSize: 'clamp(22px, 2.1vw, 28px)',
        fontWeight: 400,
        lineHeight: 1.25,
        letterSpacing: '-0.005em',
        color: P.IVY,
        margin: 0,
      }}>{title}</h3>

      {/* Excerpt */}
      <p style={{
        ...T.bodyMd,
        color: 'rgba(240,232,212,0.78)',
        textAlign: 'justify',
        hyphens: 'auto',
      }}>{excerpt}</p>

      {/* Meta line — editorial card metadata: Cormorant 700 per spec Section 3,
          GO2 alpha for dark-ground colour (BR is a cream-only bronze). */}
      <div style={{
        marginTop: 'auto',
        paddingTop: 8,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.9vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
        color: 'rgba(210, 184, 134, 0.85)',
      }}>
        <span>{date}</span>
        <span>{readTime}</span>
      </div>
    </article>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// JournalPage
// ════════════════════════════════════════════════════════════════════════════
export default function JournalPage({ onNavigate }) {
  var activeCategoryState = React.useState('All');
  var activeCategory = activeCategoryState[0];
  var setActiveCategory = activeCategoryState[1];

  var filtered = activeCategory === 'All'
    ? ARTICLES
    : ARTICLES.filter(function(a) { return a.category === activeCategory; });

  var goIntake = function() { if (onNavigate) onNavigate('intake'); };

  return (
    <div style={{ background: P.PAP, color: P.INK, fontFamily: FF }}>

      {/* ═════════ SECTION 1 — Hero (cream, page opener) ═════════ */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow center>The Cleo journal</Eyebrow>
          <div style={{ marginTop: 'clamp(24px, 2.5vw, 36px)' }}>
            <SectionHeadline
              roman="Knowledge is"
              italic="the first medicine."
            />
          </div>
          <div style={{ maxWidth: 740, margin: '0 auto' }}>
            <p style={{
              ...T.bodyLg,
              color: P.TX,
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              Clinical insights, hormonal health, and the science behind optimisation. Written by our GP team for women who want to understand their bodies, not just comply with vague advice.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 2 — Articles (dark) ═════════ */}
      <section style={{ background: P.OBS, padding: SECTION_PADDING, color: P.IVY }}>
        <div style={{ maxWidth: CONTAINER_MAX, margin: '0 auto' }}>
          <SectionDivider onDark />
          <div style={{ marginTop: 48, marginBottom: 28, textAlign: 'center' }}>
            <Eyebrow center color={P.GO2}>Clinical writing</Eyebrow>
          </div>
          <SectionHeadline
            roman="Notes from"
            italic="the consulting room."
            onDark
          />
          <div style={{ maxWidth: 820, margin: '0 auto clamp(48px, 5vw, 72px)' }}>
            <p style={{
              ...T.bodyLg,
              color: 'rgba(240,232,212,0.84)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              These are not generic wellness articles. Every piece is grounded in patterns our GPs see in practice, written to help you understand what your body might be telling you and the questions worth asking your own doctor.
            </p>
          </div>

          {/* Category filter */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 'clamp(8px, 0.9vw, 12px)',
            justifyContent: 'center',
            maxWidth: 1240,
            margin: '0 auto clamp(40px, 4.5vw, 64px)',
          }}>
            {CATEGORIES.map(function(cat) {
              return (
                <CategoryPill
                  key={cat}
                  label={cat}
                  active={activeCategory === cat}
                  onClick={function() { setActiveCategory(cat); }}
                />
              );
            })}
          </div>

          {/* Articles grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
            gap: 'clamp(20px, 2.4vw, 36px)',
            maxWidth: 1240,
            margin: '0 auto',
          }}>
            {filtered.map(function(article) {
              return <ArticleCard key={article.title} {...article} />;
            })}
          </div>

          {/* Empty state if filter yields nothing */}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 'clamp(48px, 6vw, 96px) 24px' }}>
              <p style={{
                ...T.premise,
                color: P.GO2,
                maxWidth: 480,
                margin: '0 auto',
              }}>
                No articles in this category yet. New writing arrives every month.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ═════════ SECTION 3 — Invitation (cream / SAND) ═════════
          Refactored from inline CTA to InvitationBlock atom for consistency
          with LandingPage + MembershipPage + StoriesPage. CTA verb "Start
          your health conversation" -> "Begin your health conversation"
          lands automatically via InvitationBlock default ctaLabel. */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <InvitationBlock
          eyebrowText="BEYOND READING"
          titleRoman="Reading is the start."
          titleItalic="Understanding is the work."
          subtitle={
            <React.Fragment>
              <span style={{ display: 'block' }}>
                Articles can teach you the questions to ask. A consultation gives you the answers about your own body.
              </span>
              <span style={{ display: 'block', marginTop: 6 }}>
                If something you have read here sounds familiar, a conversation with our intake specialist is the right next step.
              </span>
            </React.Fragment>
          }
          onCtaClick={goIntake}
        />
      </section>

      {/* Founding-principle close retired per doc — InvitationBlock above is
          now the closing element. */}
    </div>
  );
}
